import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Spinner,
  Center,
  Heading,
  Text,
} from '@chakra-ui/react';
import { secureFetch } from '../data/secureFetch';

const OrganizationSetup = () => {
  const { organizationId } = useSelector((state) => state.user);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const response = await secureFetch(`/api/organizations/${organizationId}`);
        const data = response;
        setOrganization(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching organization:', error);
        toast({
          title: "Error",
          description: "Failed to fetch organization data",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [organizationId, toast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrganization((prevOrg) => ({
      ...prevOrg,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const response = await secureFetch(`/api/organizations/${organizationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        data: organization,
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "Organization information updated successfully",
          status: "success",
          duration: 9000,
          isClosable: true,
        });
      } else {
        throw new Error('Failed to update organization');
      }
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({
        title: "Error",
        description: "Failed to update organization information",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  if (!organization) {
    return <Box>No organization information available.</Box>;
  }

  return (
    <Box p={5} maxW="600px" mx="auto">
      <form onSubmit={handleSubmit}>
        <Heading mb={5}>My Info</Heading>
        <Text mb={3} fontSize='2xl'>Organization Details</Text>
        <FormControl mb={4}>
          <FormLabel>Name</FormLabel>
          <Input
            name="name"
            value={organization.name}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Email</FormLabel>
          <Input
            name="email"
            value={organization.email}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Website URL</FormLabel>
          <Input
            name="website_url"
            value={organization.website_url}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Address Line 1</FormLabel>
          <Input
            name="address_line_1"
            value={organization.address_line_1}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Address Line 2</FormLabel>
          <Input
            name="address_line_2"
            value={organization.address_line_2}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>City</FormLabel>
          <Input
            name="city"
            value={organization.city}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>State</FormLabel>
          <Input
            name="state"
            value={organization.state}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Zip Code</FormLabel>
          <Input
            name="zip_code"
            value={organization.zip_code}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Description</FormLabel>
          <Textarea
            name="description"
            value={organization.description}
            onChange={handleChange}
          />
        </FormControl>
        <FormControl mb={4}>
          <FormLabel>Phone Number</FormLabel>
          <Input
            name="phone_number"
            value={organization.phone_number}
            onChange={handleChange}
          />
        </FormControl>
        <Button colorScheme="orange" type="submit" isLoading={updating}>
          Update Information
        </Button>
      </form>
    </Box>
  );
};

export default OrganizationSetup;
